import { type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label: string;
}

/**
 * Checkbox matching Figma: 18px primary-filled box with checkmark when checked.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, label, id, disabled, checked, ...props },
  ref,
) {
  const fieldId = id ?? props.name;
  const isChecked = Boolean(checked);

  return (
    <label
      htmlFor={fieldId}
      className={cn(
        'inline-flex cursor-pointer items-center gap-2.5 select-none',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <span className="relative inline-flex size-[18px] shrink-0">
        <input
          ref={ref}
          id={fieldId}
          type="checkbox"
          disabled={disabled}
          checked={checked}
          className="absolute inset-0 z-10 m-0 size-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
          {...props}
        />
        <span
          aria-hidden
          className={cn(
            'flex size-[18px] items-center justify-center rounded-sm',
            isChecked
              ? 'bg-action-primary text-[14px] leading-none font-bold text-text-on-action'
              : 'border border-border-default bg-bg-surface',
          )}
        >
          {isChecked ? '✓' : null}
        </span>
      </span>
      <span className="text-body-m text-text-primary">{label}</span>
    </label>
  );
});
